/* eslint-disable react/prop-types */
/**
 * ContactLogTab — contact events scoped to a deal.
 *
 * Brokers log each contact event with the seller (date, method, notes).
 * Unlike ApprovalsTab there is no type split — this is a single flat list,
 * so we render one WigMetadataCrudPanel.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Card, Space } from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import { stripReadOnlyForUpdate } from "../../../utils/entityUpdateBody";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import {
  dealStatusMetadataRow,
  dealStatusFormFilter,
  withDealStatus,
  splitDealStatus,
} from "../../../utils/dealStatusField";

// Strip from metadata before passing — the library panel has no `hideFields`
// prop, so pre-filtering is the supported way to suppress grid + form rows.
const HIDDEN_FIELDS = new Set(["deal_id"]);

// `contact_method` is now a managed pick list: the backend inlines its active
// values into `/metadata` as an `options` array (from the `pick_lists` table),
// so we render whatever the metadata carries — no hardcoded enum. See the
// Admin → Pick Lists page.

const ContactLogTab = ({ dealId, dealKey, dealStatus, onDealChanged }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  // POST/PUT/DELETE /deal_contact_log are [BD.BrokerAgent, BD.Landman] on the
  // server; `contactLog.edit` mirrors that list exactly. Without the gate the
  // panel offers Add/Edit/Delete to every role that can open the deal and the
  // server 403s the click.
  const { can } = usePermissions();
  const mayEdit = can("contactLog.edit");
  // `deal_status` is a field on the DEAL, not on the contact log, so it carries
  // the deal's own field-level gate — the one `DEAL_FIELD_ACTIONS` maps it to
  // and the server enforces per field. Offering it to a role the server will
  // reject turns one 403 into a failed save.
  const offerStatus = Boolean(dealKey) && can("deal.editStatus");

  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, metaResp] = await Promise.all([
        dealsService.listDealContactLog({ token: tk, userOid, dealId }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "deal_contact_log" }),
      ]);
      setRows(listResp?.data?.data || []);
      setMetadata(metaResp?.data?.data || []);
    } catch (err) {
      console.error("ContactLogTab load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredMetadata = useMemo(() => {
    const own = (metadata || []).filter((m) => !HIDDEN_FIELDS.has(m.field));
    // Only offered when there is a deal to write to — without `dealKey` the
    // PUT has no target, and a field that silently does nothing is worse than
    // one that is absent. `own.length` keeps it out of the pre-load render,
    // where a lone synthetic row would look like loaded metadata.
    if (!offerStatus || own.length === 0) return own;
    const lastOrd = own.reduce((n, m) => Math.max(n, m.ord ?? 0), 0);
    return [...own, dealStatusMetadataRow(lastOrd + 1)];
  }, [metadata, offerStatus]);

  // The dialog opens pre-filled with the deal's current status; the field is
  // `hide: true` so it never reaches the grid.
  const rowsWithStatus = useMemo(
    () => (offerStatus ? withDealStatus(rows, dealStatus) : rows),
    [rows, dealStatus, offerStatus]
  );

  /**
   * Write the deal's status, if the dialog changed it.
   *
   * Called BEFORE the entry is written, and allowed to throw: a failure here
   * aborts the save, so a contact log entry never exists implying a status
   * change that did not happen. See utils/dealStatusField.
   */
  const applyDealStatus = useCallback(
    async (status) => {
      if (status === undefined) return;
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateDeal({
        token: tk,
        userOid,
        dealKey,
        body: { deal_status: status },
      });
      if (onDealChanged) await onDealChanged();
    },
    [dealKey, getAuth, onDealChanged]
  );

  const onCreate = async (submitted) => {
    const { status, body } = splitDealStatus(submitted, dealStatus);
    await applyDealStatus(status);
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createDealContactLog({
      token: tk,
      userOid,
      body: { ...body, deal_id: dealId },
    });
    await refresh();
    return resp;
  };

  const onUpdate = async (id, submitted) => {
    const { status, body } = splitDealStatus(submitted, dealStatus);
    await applyDealStatus(status);
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateDealContactLog({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refresh();
    return resp;
  };

  const onDelete = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteDealContactLog({ token: tk, userOid, id });
    await refresh();
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {errMsg && (
        <Alert type="error" showIcon message="Failed to load contact log" description={errMsg} />
      )}
      <Card size="small" title={`Contact log (${rows.length})`}>
        <WigMetadataCrudPanel
          title="Contact log"
          rows={rowsWithStatus}
          metadata={filteredMetadata}
          formFieldFilter={dealStatusFormFilter}
          loading={loading}
          allowView
          disableNonEditable
          onCreate={mayEdit ? onCreate : undefined}
          onUpdate={mayEdit ? onUpdate : undefined}
          onDelete={mayEdit ? onDelete : undefined}
          newRowDefaults={{ deal_id: dealId }}
          emptyText="No contact events logged yet"
          editTrigger="button"
          height={260}
        />
      </Card>
    </Space>
  );
};

export default ContactLogTab;
